import moment from "moment";
import { esc, printHtml, tableHtml } from "../../utils/printDoc";
import { parseFolliDate, readFolliculogramVersions } from "./Folliculogram";

// Printouts and summaries for one treatment cycle, in the layout of the
// clinical Letters & Templates "medication schedule": the cycle, the drugs by
// day, and the appointments that fall in it.

const SIZE_LABELS = [">25", ...Array.from({ length: 16 }, (_, i) => String(25 - i)), "6-9", "2-5", "Total"];

export function latestDays(json) {
  const versions = readFolliculogramVersions(json);
  const v = versions[versions.length - 1];
  return v ? { days: v.days, number: v.number, count: versions.length } : { days: [], number: null, count: 0 };
}

const dateOf = (d) => parseFolliDate(d.date);

export function cycleSpan(days) {
  const dates = days.map(dateOf).filter(Boolean).sort((a, b) => a - b);
  if (!dates.length) return null;
  const from = moment(dates[0]);
  const to = moment(dates[dates.length - 1]);
  return { from, to, count: to.diff(from, "days") + 1 };
}

// Follicles seen in total, right and left, across the scan days.
export function follicleTotals(days) {
  let right = 0;
  let left = 0;
  days.forEach((d) => {
    const total = (d.Values || [])[SIZE_LABELS.length - 1] || {};
    right += parseInt(total.Col_R, 10) || 0;
    left += parseInt(total.Col_l ?? total.Col_L, 10) || 0;
  });
  return { right, left };
}

const dayLabel = (d) => {
  const m = dateOf(d);
  return m ? `${moment(m).format("ddd")}<br>${moment(m).format("DD-MMM")}` : esc(d.date);
};

// Drugs by name across the days, as the folliculogram records them.
function drugRows(days) {
  const names = [];
  days.forEach((d) => (d.DrugsValue || []).forEach((x) => {
    if (x?.DrugName && !names.includes(x.DrugName)) names.push(x.DrugName);
  }));
  return names.map((name) => [
    name,
    ...days.map((d) => {
      const hit = (d.DrugsValue || []).find((x) => x?.DrugName === name);
      return hit?.DrugsVAlues ?? hit?.DrugsValues ?? "";
    }),
  ]);
}

const chunk = (list, n) => Array.from({ length: Math.ceil(list.length / n) }, (_, i) => list.slice(i * n, (i + 1) * n));

function dayTable(days, rows, label) {
  return chunk(days, 10)
    .map((part, i) => {
      const offset = i * 10;
      const headers = [label, ...part.map((d) => ({ html: dayLabel(d) }))];
      const head = `<th>${esc(label)}</th>${part.map((d) => `<th>${dayLabel(d)}</th>`).join("")}`;
      const body = rows
        .map((r) => `<tr><td><strong>${esc(r[0])}</strong></td>${part.map((_, j) => `<td>${esc(r[1 + offset + j] ?? "")}</td>`).join("")}</tr>`)
        .join("");
      return headers && `<table class="wide"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
    })
    .join("");
}

export function appointmentsRows(appointments) {
  return appointments.map((a) => [
    moment(a.startTime).format("ddd DD/MM/YYYY HH:mm"),
    a.duration ? `${a.duration} min` : "",
    a.subject || a.appointmentlibraryitemName || "Appointment",
    a.roomName || "",
    a.bookingStatusName || "",
  ]);
}

// The cycle's appointments: those inside the folliculogram's dates (a few days
// either side), or all the upcoming ones when it has no dates yet.
export function appointmentsIn(appointments, span) {
  const list = [...appointments].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  if (!span) return list.filter((a) => moment(a.startTime).isSameOrAfter(moment().startOf("day")));
  const from = span.from.clone().subtract(3, "days").startOf("day");
  const to = span.to.clone().add(3, "days").endOf("day");
  return list.filter((a) => moment(a.startTime).isBetween(from, to, undefined, "[]"));
}

// "Medication schedule": cycle header, drugs by day, appointments.
export function printSchedule({ title, couple, type, start, cycle, appointments }) {
  const { days } = latestDays(cycle?.folliculagramJSON);
  const span = cycleSpan(days);
  const drugs = drugRows(days);
  const appts = appointmentsIn(appointments || [], span);

  const body = `
    <div class="kv">
      <div><span>Treatment</span>${esc(title)}</div>
      <div><span>Couple</span>${esc(couple || "")}</div>
      <div><span>Type</span>${esc(type || "")}</div>
      <div><span>Dates</span>${span ? `${span.from.format("DD-MMM-YYYY")} – ${span.to.format("DD-MMM-YYYY")} (${span.count} days)` : esc(start || "")}</div>
    </div>
    <h2>Drug schedule</h2>
    ${drugs.length ? dayTable(days, drugs, "Drug") : '<p class="muted">No drugs have been scheduled for this treatment yet.</p>'}
    <h2>Appointments</h2>
    ${appts.length ? tableHtml(["When", "Length", "What", "Where", "Status"], appointmentsRows(appts)) : '<p class="muted">No appointments fall in this treatment.</p>'}
    <p class="muted">Doses are as planned by your clinic and may change. Follow the instructions you are given.</p>`;
  return printHtml({ title: "Medication schedule", subtitle: title, bodyHtml: body });
}

// The folliculogram, printed in the layout of the screen: scan days across
// (D1, DAY 1 (SAT), date, then R and L), follicles by size, then hormones and
// drugs, then the key dates, the appointments and the drug regime by date.
// Landscape, fourteen days to a page, empty rows and days left out.
const FOLLI_CSS = `
  body { font-size: 9.5px; }
  .keys { display: flex; flex-wrap: wrap; gap: 4px 6px; margin: 0 0 8px; }
  .keys b { font-size: 8.5px; text-transform: uppercase; letter-spacing: .05em; color: #5f6f80; align-self: center; margin-right: 4px; }
  .pill { border: 1px solid #b7d8d1; background: #e6f2ef; border-radius: 999px; padding: 1px 8px; font-size: 9px; }
  .pill.visit { border-color: #8aa8d8; background: #eef3fb; }
  .pill em { font-style: normal; color: #a65b00; font-weight: 700; margin-left: 3px; }
  table.fg { table-layout: fixed; }
  table.fg th, table.fg td { padding: 2px 3px; text-align: center; font-size: 9px; }
  table.fg th.lab, table.fg td.lab { text-align: left; width: 78px; font-weight: 600; background: #f1f5f8; }
  table.fg th.d { background: #e6f2ef; color: #256a5f; }
  table.fg th.dt { font-weight: 500; line-height: 1.15; }
  table.fg th.dt b { display: block; font-size: 8.5px; }
  table.fg td.r { background: #fdf3f3; } table.fg td.l { background: #f1f6fd; }
  table.fg th.rr { background: #fbe3e3; color: #b3261e; } table.fg th.ll { background: #dce9fb; color: #1f4f8f; }
  table.fg tr.tot td { font-weight: 700; }
  table.fg tr.sec td { background: #eef2f5; text-align: left; font-weight: 700; text-transform: uppercase; font-size: 8px; letter-spacing: .05em; color: #5f6f80; }
  table.fg td.dose { background: #e6f2ef; font-weight: 600; }
  .pb { break-before: page; }
`;

export function printFolliculogram({ title, couple, type, cycle, appointments = [], keyDates }) {
  const { days, number, count } = latestDays(cycle?.folliculagramJSON);
  const span = cycleSpan(days);
  const totals = follicleTotals(days);
  const keys = keyDates || keyDatesOf(cycle, span?.from);
  const visits = appointmentsIn(appointments, span);
  const dayNo = (m) => (span ? moment(m).startOf("day").diff(span.from.clone().startOf("day"), "days") + 1 : null);

  const has = (v) => v !== null && v !== undefined && String(v).trim() !== "";
  const val = (d, i, col) => (d.Values || [])[i]?.[col];
  const hormones = [["Endometrium", "Endome"], ["E2", "E2"], ["LH", "LH"], ["P4", "p4"], ["FSH", "FSH"], ["Prolactin", "PROL"]];
  const drugs = [];
  days.forEach((d) => (d.DrugsValue || []).forEach((x) => { if (x?.DrugName && !drugs.includes(x.DrugName)) drugs.push(x.DrugName); }));
  const drugAt = (d, name) => {
    const hit = (d.DrugsValue || []).find((x) => x?.DrugName === name);
    return hit?.DrugsVAlues ?? hit?.DrugsValues ?? "";
  };

  // A day is left out when nothing is recorded on it and nothing is booked.
  const used = days.filter((d, i) => {
    const m = dateOf(d);
    return SIZE_LABELS.some((_, r) => has(val(d, r, "Col_R")) || has(val(d, r, "Col_l"))) ||
      hormones.some(([, k]) => has(d[k])) || drugs.some((n) => has(drugAt(d, n))) ||
      (m && (keys.some((k) => k.date.isSame(m, "day")) || visits.some((v) => moment(v.startTime).isSame(m, "day"))));
  });
  const sizeRows = SIZE_LABELS.map((label, r) => ({ label, r })).filter(({ r }) => r === SIZE_LABELS.length - 1 || used.some((d) => has(val(d, r, "Col_R")) || has(val(d, r, "Col_l"))));
  const hormoneRows = hormones.filter(([, k]) => used.some((d) => has(d[k])));
  const drugRowsShown = drugs.filter((n) => used.some((d) => has(drugAt(d, n))));

  const tables = chunk(used, 14).map((part, pi) => {
    const head1 = `<th class="lab">Day</th>${part.map((d) => { const m = dateOf(d); return `<th colspan="2" class="d">D${m ? dayNo(m) : ""}</th>`; }).join("")}`;
    const head2 = `<th class="lab">Date</th>${part.map((d) => { const m = dateOf(d); return `<th colspan="2" class="dt">${m ? `DAY ${dayNo(m)} (${moment(m).format("ddd").toUpperCase()})<b>${moment(m).format("DD-MMM-YYYY").toUpperCase()}</b>` : esc(d.date)}</th>`; }).join("")}`;
    const head3 = `<th class="lab">In mm</th>${part.map(() => '<th class="rr">R</th><th class="ll">L</th>').join("")}`;
    const sizeBody = sizeRows.map(({ label, r }) => `<tr class="${r === SIZE_LABELS.length - 1 ? "tot" : ""}"><td class="lab">${esc(label)}</td>${part.map((d) => `<td class="r">${esc(val(d, r, "Col_R") ?? "")}</td><td class="l">${esc(val(d, r, "Col_l") ?? "")}</td>`).join("")}</tr>`).join("");
    const wide = (label, cells) => `<tr><td class="lab">${esc(label)}</td>${cells.map((c) => `<td colspan="2" class="${c ? "dose" : ""}">${esc(c)}</td>`).join("")}</tr>`;
    const hormoneBody = hormoneRows.length ? `<tr class="sec"><td colspan="${1 + part.length * 2}">Hormones</td></tr>${hormoneRows.map(([label, k]) => wide(label, part.map((d) => d[k] ?? ""))).join("")}` : "";
    const drugBody = drugRowsShown.length ? `<tr class="sec"><td colspan="${1 + part.length * 2}">Drugs</td></tr>${drugRowsShown.map((n) => wide(n, part.map((d) => drugAt(d, n)))).join("")}` : "";
    const marks = `<tr class="sec"><td colspan="${1 + part.length * 2}">Key dates and visits</td></tr><tr><td class="lab">On the day</td>${part.map((d) => {
      const m = dateOf(d);
      const here = [
        ...keys.filter((k) => m && k.date.isSame(m, "day")).map((k) => k.label),
        ...visits.filter((v) => m && moment(v.startTime).isSame(m, "day")).map((v) => `${moment(v.startTime).format("HH:mm")} ${v.appointmentlibraryitemName || v.subject || "Appointment"}`),
      ];
      return `<td colspan="2" style="font-size:8px;text-align:left">${here.map(esc).join("<br>")}</td>`;
    }).join("")}</tr>`;
    return `<table class="wide fg ${pi ? "pb" : ""}"><thead><tr>${head1}</tr><tr>${head2}</tr><tr>${head3}</tr></thead><tbody>${sizeBody}${hormoneBody}${drugBody}${marks}</tbody></table>`;
  }).join("");

  const regimeDates = [...new Set(days.filter((d) => dateOf(d) && drugs.some((n) => has(drugAt(d, n)))).map((d) => moment(dateOf(d)).format("YYYY-MM-DD")))].sort();
  const regime = regimeDates.length
    ? tableHtml(["Date", ...drugs], regimeDates.map((k) => {
        const d = days.find((x) => dateOf(x) && moment(dateOf(x)).format("YYYY-MM-DD") === k);
        return [`${moment(k).format("ddd DD-MMM-YYYY")} (D${dayNo(moment(k))})`, ...drugs.map((n) => drugAt(d, n))];
      }))
    : '<p class="muted">No drugs have been scheduled yet.</p>';

  const body = `
    <div class="kv">
      <div><span>Treatment</span>${esc(title)}</div>
      <div><span>Type</span>${esc(type || "")}</div>
      <div><span>Dates</span>${span ? `${span.from.format("DD-MMM-YYYY")} – ${span.to.format("DD-MMM-YYYY")} (${span.count} days)` : ""}</div>
      <div><span>Version</span>${number ?? ""}${count > 1 ? ` of ${count}` : ""}</div>
      <div><span>Right follicles</span>${totals.right}</div>
      <div><span>Left follicles</span>${totals.left}</div>
      <div><span>Couple</span>${esc(couple || "")}</div>
    </div>
    ${keys.length ? `<div class="keys"><b>Key dates</b>${keys.map((k) => `<span class="pill">${esc(k.label)} ${k.date.format("DD-MMM")}${k.day ? `<em>D${k.day}</em>` : ""}</span>`).join("")}</div>` : ""}
    ${visits.length ? `<div class="keys"><b>Visits</b>${visits.map((v) => `<span class="pill visit">${esc(v.appointmentlibraryitemName || v.subject || "Appointment")} ${moment(v.startTime).format("DD-MMM")}${span ? `<em>D${dayNo(v.startTime)}</em>` : ""}</span>`).join("")}</div>` : ""}
    <h2>Folliculogram</h2>
    ${used.length ? tables : '<p class="muted">No folliculogram has been recorded for this treatment yet.</p>'}
    <div class="pb"></div>
    <h2>Appointments in this treatment</h2>
    ${visits.length ? tableHtml(["When", "Length", "What", "Where", "Status"], appointmentsRows(visits)) : '<p class="muted">No appointments are booked in this treatment.</p>'}
    <h2>Drug regime by date</h2>
    ${regime}`;
  return printHtml({ title: "Folliculogram", subtitle: title, bodyHtml: body, landscape: true, extraCss: FOLLI_CSS });
}

// Dates the cycle record carries, as "key dates" chips, with the day of the
// cycle each falls on (D1 is the first folliculogram day).
const KEY_DATES = [
  ["First injection", /^firstinjectiondate$/i],
  ["Trigger", /trigger.*(date|time)|(date|time).*trigger/i],
  ["Egg collection", /(egg|oocyte).*(collect|retriev).*date|date.*(egg|oocyte).*(collect|retriev)/i],
  ["Fertilisation check", /fertili[sz]ation.*check/i],
  ["Embryo transfer", /transfer.*date|date.*transfer/i],
  ["Pregnancy test", /early_outcome|pregnancy.*test.*date|date.*pregnancy.*test/i],
];

export function keyDatesOf(cycle, firstDay) {
  const out = [];
  if (!cycle) return out;
  const d1 = firstDay ? moment(firstDay).startOf("day") : null;
  KEY_DATES.forEach(([label, re]) => {
    const key = Object.keys(cycle).find((k) => re.test(k) && cycle[k] && !String(cycle[k]).startsWith("0001-01-01") && moment(cycle[k]).isValid());
    if (!key) return;
    const m = moment(cycle[key]);
    out.push({ label, date: m, day: d1 ? m.clone().startOf("day").diff(d1, "days") + 1 : null });
  });
  return out.sort((a, b) => a.date - b.date);
}


// ── The wall chart ──────────────────────────────────────────────────────────
//
// One landscape page to stick on the wall and tick off: a calendar of the
// treatment, a box per day with the date and the day of treatment, each dose to
// take (with a tick box), the appointments booked, and the key dates. Weeks run
// Monday to Sunday.

const WALL_CSS = `
  body { font-size: 10px; }
  header.doc { padding-bottom: 6px; margin-bottom: 8px; }
  header.doc h1 { font-size: 22px; }
  .legend { display: flex; gap: 14px; margin: 0 0 6px; font-size: 9.5px; color: #5f6f80; }
  .legend i { display: inline-block; width: 10px; height: 10px; border-radius: 2px; margin-right: 4px; vertical-align: -1px; }
  table.cal { table-layout: fixed; border-collapse: separate; border-spacing: 3px; }
  table.cal th { background: #2f7f72; color: #fff; border: 0; text-align: center; padding: 3px; border-radius: 3px; }
  table.cal td { height: 31mm; border: 1.2px solid #b7d8d1; border-radius: 5px; padding: 3px 4px; vertical-align: top; background: #fff; }
  table.cal td.out { border: 1px dashed #e3e8ee; background: #fafbfc; }
  table.cal td.key { background: #fdf6e3; border-color: #d1a24a; }
  .dn { display: flex; justify-content: space-between; align-items: baseline; font-weight: 700; font-size: 12px; }
  .dn small { font-weight: 600; font-size: 8.5px; color: #256a5f; letter-spacing: .04em; }
  .mo { font-size: 8px; color: #5f6f80; font-weight: 600; text-transform: uppercase; }
  .dose { display: flex; align-items: center; gap: 4px; font-size: 9.5px; margin-top: 2px; }
  .box { width: 9px; height: 9px; border: 1.2px solid #2f7f72; border-radius: 2px; flex: none; }
  .appt { margin-top: 2px; padding: 1px 4px; border-radius: 3px; background: #eef3fb; color: #1f4f8f; font-size: 9px; font-weight: 600; }
  .badge { margin-top: 2px; padding: 1px 4px; border-radius: 3px; background: #d1a24a; color: #fff; font-size: 9px; font-weight: 700; }
  .notes { margin-top: 8px; border: 1px solid #d6dde5; border-radius: 5px; height: 18mm; padding: 3px 6px; color: #5f6f80; font-size: 9px; }
`;

export function printWallChart({ title, couple, type, cycle, appointments, keyDates = [] }) {
  const { days } = latestDays(cycle?.folliculagramJSON);
  const span = cycleSpan(days);
  if (!span) return printSchedule({ title, couple, type, cycle, appointments });

  const key = keyDates.length ? keyDates : keyDatesOf(cycle, span.from);
  const appts = (appointments || []).filter((a) => moment(a.startTime).isBetween(span.from.clone().startOf("day"), span.to.clone().add(7, "days").endOf("day"), undefined, "[]"));

  // The calendar runs to the last booked or key date within a week of the end.
  const lastDate = [span.to, ...key.map((k) => k.date), ...appts.map((a) => moment(a.startTime))]
    .reduce((m, d) => (d.isAfter(m) ? d : m), span.to).clone().startOf("day");
  const first = span.from.clone().startOf("isoWeek");
  const last = lastDate.clone().endOf("isoWeek");

  const byDate = {};
  days.forEach((d, i) => {
    const m = dateOf(d);
    if (m) byDate[moment(m).format("YYYY-MM-DD")] = d;
  });
  const drugNamesAll = [];
  days.forEach((d) => (d.DrugsValue || []).forEach((x) => { if (x?.DrugName && !drugNamesAll.includes(x.DrugName)) drugNamesAll.push(x.DrugName); }));

  const cell = (m) => {
    const k = m.format("YYYY-MM-DD");
    const inCycle = m.isBetween(span.from.clone().startOf("day"), lastDate.clone().endOf("day"), undefined, "[]");
    if (!inCycle) return '<td class="out"></td>';
    const day = byDate[k];
    const doses = day ? (day.DrugsValue || []).filter((x) => x?.DrugName && String(x.DrugsVAlues ?? x.DrugsValues ?? "").trim() !== "") : [];
    const ks = key.filter((x) => x.date.isSame(m, "day"));
    const as = appts.filter((a) => moment(a.startTime).isSame(m, "day"));
    const dayNo = m.diff(span.from.clone().startOf("day"), "days") + 1;
    return `<td class="${ks.length ? "key" : ""}">
      <div class="dn"><span>${m.format("D")}${m.date() === 1 || m.isSame(span.from, "day") ? ` <span class="mo">${m.format("MMM")}</span>` : ""}</span><small>DAY ${dayNo}</small></div>
      ${ks.map((x) => `<div class="badge">${esc(x.label)}</div>`).join("")}
      ${as.map((a) => `<div class="appt">${esc(moment(a.startTime).format("HH:mm"))} ${esc(a.appointmentlibraryitemName || a.subject || "Appointment")}</div>`).join("")}
      ${doses.map((x) => `<div class="dose"><span class="box"></span><span>${esc(x.DrugName)} <b>${esc(x.DrugsVAlues ?? x.DrugsValues)}</b></span></div>`).join("")}
    </td>`;
  };

  const weeks = [];
  for (let w = first.clone(); w.isBefore(last); w.add(1, "week")) {
    weeks.push(`<tr>${Array.from({ length: 7 }, (_, i) => cell(w.clone().add(i, "days"))).join("")}</tr>`);
  }

  const body = `
    <div class="legend">
      <span><i style="background:#d1a24a"></i>Key date</span>
      <span><i style="background:#eef3fb;border:1px solid #8aa8d8"></i>Appointment</span>
      <span><i style="background:#fff;border:1.2px solid #2f7f72"></i>Tick when taken</span>
      <span>${esc(title)} &middot; ${span.from.format("DD-MMM-YYYY")} – ${span.to.format("DD-MMM-YYYY")}</span>
    </div>
    <table class="cal"><thead><tr>${["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => `<th>${d}</th>`).join("")}</tr></thead><tbody>${weeks.join("")}</tbody></table>
    <div class="notes">My notes</div>`;
  return printHtml({ title: "My treatment plan", subtitle: `${type || ""} ${couple ? `· ${couple}` : ""}`.trim(), bodyHtml: body, landscape: true, extraCss: WALL_CSS });
}
